<template>
  <v-card class="room-timetable" :loading="loading">
    <div class="hero-panel">
      <v-card-title class="hero-header">
        <div>
          <h1 class="text-h5 font-weight-bold hero-title">{{ $t('room_timetable.title') }}</h1>
          <div class="text-body-2 hero-subtitle mt-2">{{ $t('room_timetable.subtitle') }}</div>
        </div>
      </v-card-title>
    </div>
    <v-card-text class="content-shell">
      <v-alert v-if="error" type="error" outlined class="feedback-alert">
        {{ $t('teach_table.error') }}
        <v-btn small text @click="loadQuery">{{ $t('room_timetable.retry') }}</v-btn>
      </v-alert>
      <v-alert v-else-if="!loading && !result" type="info" outlined class="feedback-alert">
        {{ $t('room_timetable.invalid_link') }}
      </v-alert>
      <v-progress-linear v-if="loading" indeterminate color="deep-orange lighten-2" class="mb-4" />

      <template v-if="result && !loading && !error">
        <v-sheet class="summary-panel pa-4 mb-4 d-flex flex-wrap align-center summary-row">
          <div>
            <h2 class="text-h6 brown--text">{{ roomTitle }}</h2>
            <div class="mt-1">
              {{ $t('room_timetable.building') }} {{ buildingTitle }}
              <span v-if="Number(result.room.capacity) > 0"> · {{ $t('room_timetable.capacity', { count: result.room.capacity }) }}</span>
            </div>
            <div class="d-flex flex-wrap mt-3 metadata-row">
              <v-chip small class="meta-chip mr-2 mb-1" color="amber lighten-5" text-color="deep-orange darken-2"><v-icon left small>mdi-calendar-range</v-icon>{{ $t('student_timetable.year_semester') }}: {{ termLabel }}</v-chip>
              <v-chip small class="meta-chip mb-1" color="orange lighten-5" text-color="brown darken-1"><v-icon left small>mdi-update</v-icon>{{ $t('student_timetable.updated_at') }}: {{ formattedUpdatedAt }}</v-chip>
            </div>
          </div>
          <v-spacer />
          <div class="download-actions">
            <v-btn v-for="type in ['jpg', 'pdf']" :key="type" small :outlined="type === 'jpg'" color="deep-orange" class="ml-2 download-action" :class="{ 'white--text': type === 'pdf' }" :elevation="type === 'pdf' ? 4 : 3"
              :loading="downloadingType === type" :disabled="!!downloadingType" @click="downloadTimetable(type)">
              <v-icon left small>{{ type === 'pdf' ? 'mdi-file-pdf-box' : 'mdi-file-image-outline' }}</v-icon>{{ type.toUpperCase() }}
            </v-btn>
          </div>
        </v-sheet>
        <v-alert v-if="!result.subjects.length" type="info" dense outlined>{{ $t('room_timetable.empty') }}</v-alert>
        <div class="d-flex flex-wrap mb-3 legend">
          <v-chip small class="mr-2 mb-1 undergraduate">{{ $t('room_timetable.undergraduate') }}</v-chip>
          <v-chip small class="mb-1 graduate">{{ $t('room_timetable.graduate') }}</v-chip>
          <span class="text-caption ml-3">{{ $t('room_timetable.detail_hint') }}</span>
        </div>
        <v-sheet ref="scheduleExport" class="schedule-panel">
          <div class="schedule-wrap">
            <div class="schedule-grid" :style="gridStyle">
              <div class="corner-cell">{{ $t('room_timetable.day') }}</div>
              <div v-for="hour in hours" :key="hour" class="time-cell">{{ clock(hour) }} - {{ clock(hour + 1) }}</div>
              <div v-for="day in layout.days" :key="day.day" class="day-fragment">
                <div class="day-cell">{{ dayName(day.day) }}</div>
                <div class="day-row" :style="{ gridColumn: 'span ' + hours.length, height: day.height + 'px' }">
                  <div v-for="n in hours.length * 4 - 1" :key="n" class="quarter-line" :style="{ left: (n / (hours.length * 4)) * 100 + '%' }" />
                  <button v-for="event in day.events" :key="event.key" class="class-block class-block--clickable" type="button"
                    :class="Number(event.subject.level_id) === 1 ? 'undergraduate' : 'graduate'"
                    :style="event.style" v-tooltip="eventTitle(event)" @click="showingSubject = event.subject">
                    <span class="class-code">{{ event.subject.subject_id }}</span>
                    <span class="class-name">{{ subjectName(event.subject) }}</span>
                    <span class="class-meta">{{ $t('teach_table.section') }} {{ event.subject.section }} · ({{ levelName(event.subject) }})</span>
                    <span class="class-room">{{ teacherNames(event.subject).join(', ') || '–' }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </v-sheet>

        <v-sheet v-if="result.subjects.length" ref="tableExport" class="table-panel mt-4">
          <v-simple-table dense class="subject-table">
            <thead><tr>
              <th class="text-center">{{ $t('teach_table.course_id') }}</th><th>{{ $t('teach_table.course_name') }}</th>
              <th class="text-center">{{ $t('room_timetable.level') }}</th><th class="text-center">{{ $t('teach_table.section') }}</th>
              <th>{{ $t('teach_table.teaching_date') }}</th><th>{{ $t('teach_table.lecturer') }}</th>
            </tr></thead>
            <tbody><tr v-for="subject in result.subjects" :key="subject.row_key">
              <td class="text-center">{{ subject.subject_id }}</td>
              <td><button class="subject-link" type="button" @click="openSubjectDetails(subject)">{{ subjectName(subject) || subject.subject_id }}</button></td>
              <td class="level-cell text-center">{{ levelName(subject) }}</td><td class="text-center">{{ subject.section }}<div><lect-or-prac-chip :item="subject" /></div></td>
              <td><div v-for="(meeting, index) in subject.teach_days" :key="index" class="meeting">{{ meetingText(meeting) }}</div>
                <span v-if="!subject.teach_days.length">{{ $t('room_timetable.no_time') }}</span></td>
              <td><div v-for="(name, index) in teacherNames(subject)" :key="index">{{ name }}</div><span v-if="!teacherNames(subject).length">–</span></td>
            </tr></tbody>
          </v-simple-table>
        </v-sheet>
      </template>

      <timetable-day-dialog :value="!!showingSubject" :subject="showingSubject"
        :level-id="showingSubject ? showingSubject.level_id : null" :teachers="showingSubject ? teacherNames(showingSubject) : []"
        @input="!$event && (showingSubject = null)" @details="openSubjectDetails" />
      <subject-dialog ref="subjectDialog" :level-id="detailLevel" />
    </v-card-text>
  </v-card>
</template>

<script>
import { dateTimeFromDBTranslatorFull } from '@/helper/translator';
import RoomTimetableProvider from '@/services/room_timetable.provider';
import LectOrPracChip from '@/views/teach_table/components/LectOrPracChip.vue';
import TimetableDayDialog from './TimetableDayDialog.vue';
import SubjectDialog from '@/views/teach_table/components/SubjectDialog.vue';
import { buildRoomTimetable } from './roomTimetableLayout';
import { createPrintPage, renderExportElementToCanvas, saveJpgPages, savePdfPages } from './timetableExport';

const service = new RoomTimetableProvider();
const queryText = value => typeof value === 'string' ? value : '';

export default {
  name: 'RoomTimetable',
  components: { SubjectDialog, LectOrPracChip, TimetableDayDialog },
  data: () => ({
    selectedTerm: null, buildingNo: null, roomNo: null,
    result: null, loading: false, error: false, requestId: 0,
    downloadingType: null, showingSubject: null, detailLevel: '1',
  }),
  computed: {
    termLabel() {
      if (!this.selectedTerm) return '';
      const [year, semester] = this.selectedTerm.split('-');
      return semester + ' / ' + (this.$i18n.locale === 'en' ? Number(year) - 543 : year);
    },
    formattedUpdatedAt() { return this.result ? dateTimeFromDBTranslatorFull(this.result.updated_at) : ''; },
    layout() { return buildRoomTimetable(this.result ? this.result.subjects : []); },
    hours() { return Array.from({ length: this.layout.endHour - this.layout.startHour }, (_, i) => i + this.layout.startHour); },
    gridStyle() { return { gridTemplateColumns: `90px repeat(${this.hours.length}, minmax(0, 1fr))` }; },
    roomTitle() { return this.result ? this.result.room.room_name || this.result.room.room_no : ''; },
    buildingTitle() { return this.result ? this.result.room.building_name || this.result.room.building_no : ''; },
    exportMetadata() {
      return {
        title: this.$t('room_timetable.title'), studentIdLabel: this.$t('room_timetable.room'), studentId: this.roomTitle,
        studentNameLabel: this.$t('room_timetable.building'), studentName: this.buildingTitle,
        termLabel: this.$t('student_timetable.year_semester'),
        term: this.termLabel,
        updatedAtLabel: this.$t('student_timetable.updated_at'), updatedAt: this.formattedUpdatedAt,
      };
    },
  },
  watch: { '$route.query': 'loadQuery' },
  created() { this.loadQuery(); },
  beforeDestroy() { this.requestId += 1; },
  methods: {
    clock(hour) { return `${String(hour).padStart(2, '0')}:00`; },
    dayName(day) { return this.$t(`student_timetable.${['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][Number(day) - 1]}`); },
    subjectName(subject) { return subject.subject_ename || subject.subject_name_en || ''; },
    levelName(subject) { return this.$t(Number(subject.level_id) === 1 ? 'room_timetable.undergraduate' : 'room_timetable.graduate'); },
    teacherNames(subject) { return (subject.teachers || []).map(teacher => this.$i18n.locale === 'en' ? teacher.name_en || teacher.name_th : teacher.name_th || teacher.name_en).filter(Boolean); },
    meetingText(meeting) { return `${this.dayName(meeting.day)} ${meeting.time} – ${meeting.time2}`; },
    eventTitle(event) { return [event.subject.subject_id, this.subjectName(event.subject), this.levelName(event.subject), this.meetingText(event.meeting), ...this.teacherNames(event.subject)].join(' | '); },
    async openSubjectDetails(subject) {
      this.detailLevel = String(subject.level_id);
      const subjectId = subject.subject_id;
      await this.$nextTick();
      this.$refs.subjectDialog.show_item_detail(subjectId);
    },
    async loadQuery() {
      const requestId = ++this.requestId;
      this.loading = true;
      this.error = false;
      this.result = null;
      this.showingSubject = null;
      try {
        const query = this.$route.query;
        const year = queryText(query.year);
        const semester = queryText(query.semester);
        this.buildingNo = queryText(query.building_no).trim();
        this.roomNo = queryText(query.room_no).trim();
        this.selectedTerm = null;
        if (!/^[1-9][0-9]{3}$/.test(year) || !/^[123]$/.test(semester) || !this.buildingNo || !this.roomNo) return;
        this.selectedTerm = year + '-' + semester;
        const result = await service.getTimetable({ year, semester, building_no: this.buildingNo, room_no: this.roomNo });
        if (requestId !== this.requestId) return;
        this.result = result;
      } catch (error) {
        if (requestId === this.requestId) {
          this.error = true;
          const message = error.response && error.response.data && error.response.data.message;
          this.$toasted.error(message ? JSON.stringify(message) : this.$t('teach_table.error'), { duration: 0 });
        }
      } finally {
        if (requestId === this.requestId) { this.loading = false; }
      }
    },
    async downloadTimetable(type) {
      if (!this.result || !this.$refs.scheduleExport) return;
      this.downloadingType = type;
      try {
        await this.$nextTick();
        const pages = [];
        for (const ref of ['scheduleExport', 'tableExport']) {
          if (!this.$refs[ref]) continue;
          const canvas = await renderExportElementToCanvas(this.$refs[ref], this.exportMetadata, { orientation: 'landscape' });
          pages.push({ canvas: createPrintPage(canvas, 'landscape'), orientation: 'landscape', fileSuffix: ref });
        }
        const filename = `room-timetable-${this.selectedTerm}-${this.buildingNo}-${this.roomNo}`.replace(/[\\/:*?"<>|]/g, '-');
        if (type === 'pdf') savePdfPages(pages, filename);
        else await saveJpgPages(pages, filename);
      } catch (error) {
        this.$toasted.error(this.$t('room_timetable.export_error'), { duration: 5000 });
      } finally { this.downloadingType = null; }
    },
  },
};
</script>

<style scoped>
.room-timetable {
  max-width: 1440px;
  margin: 0 auto;
  background: #fffdfb;
}

.hero-panel {
  background: #fff3ea;
  border-bottom: 1px solid rgba(224, 182, 146, 0.42);
}

.hero-header {
  gap: 16px;
  padding: 28px 32px 24px;
}

.hero-copy {
  max-width: 680px;
}

.hero-title {
  color: #5d4036;
  font-family: "Anuphan", sans-serif !important;
  letter-spacing: 0;
}

.hero-subtitle {
  color: #87685d;
  max-width: 54ch;
}

.content-shell {
  padding: 24px;
}

.summary-panel,
.schedule-panel,
.table-panel {
  overflow: hidden;
  border: 1px solid #f1e1d6;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 16px 30px rgba(171, 126, 97, 0.08);
}

.summary-panel {
  background: #fff8f4;
}

.soft-field {
  border-radius: 10px;
}

.search-btn {
  width: 100%;
  max-width: 168px;
  height: 42px !important;
  letter-spacing: 0.01em;
}

.meta-chip {
  font-weight: 500;
}

.updated-at-col {
  margin-left: auto;
}

.download-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin-left: auto;
}

.download-action {
  box-shadow: 0 6px 14px rgba(216, 91, 43, 0.18) !important;
}

.teacher-line {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.teach-time {
  display: flex;
  width: max-content;
  align-items: center;
  white-space: nowrap;
}

.feedback-alert {
  border-radius: 10px;
}

.feedback-alert--error {
  background: #fff4f2 !important;
}

.feedback-alert--info {
  background: #f8fbff !important;
}

.schedule-wrap {
  overflow-x: hidden;
  overflow-y: hidden;
  background: #fffdfb;
  -webkit-overflow-scrolling: touch;
}

.schedule-grid {
  display: grid;
  width: 100%;
  min-width: 0;
  grid-template-columns: 90px repeat(13, minmax(0, 1fr));
}

.schedule-grid,
.schedule-grid * {
  box-sizing: border-box;
}

.day-fragment {
  display: contents;
}

.corner-cell,
.time-cell,
.day-cell {
  background: #fdf0e5;
  border-right: 1px solid #ead2c0;
  border-bottom: 1px solid #ead2c0;
  color: #614439;
  font-family: "Sarabun", sans-serif;
  font-weight: 700;
}

.corner-cell,
.time-cell {
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.time-cell {
  padding: 0 4px;
  font-size: 12px;
  line-height: 1.15;
  text-align: center;
  white-space: normal;
}

.day-cell {
  min-height: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff7f1;
  font-size: 14px;
  padding: 0 4px;
  text-align: center;
}

.day-row {
  grid-column: span 13;
  position: relative;
  min-height: 54px;
  overflow: visible;
  border-bottom: 1px solid #f0e6df;
  background: #fffdfb;
}

.quarter-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(149, 120, 100, 0.04);
  pointer-events: none;
}

.quarter-line:nth-child(4n) {
  background: rgba(149, 120, 100, 0.12);
}

.block-sunday {
  background: #f8dede;
}

.block-monday {
  background: #f7efc8;
}

.block-tuesday {
  background: #f5dff0;
}

.block-wednesday {
  background: #dff1de;
}

.block-thursday {
  background: #f9e3cc;
}

.block-friday {
  background: #dce9f7;
}

.block-saturday {
  background: #e9def8;
}

.subject-table {
  background: transparent;
}

.subject-table thead {
  background: #fdf0e1;
}

.subject-table th {
  color: #5d4036 !important;
  font-family: "Sarabun", sans-serif !important;
  font-size: 12px !important;
  letter-spacing: 0;
  text-transform: uppercase;
  padding: 4px 10px !important;
  border-bottom: none !important;
}

.subject-table td {
  border-color: #f3e6dc !important;
  padding: 4px 10px !important;
}

.subject-table tbody tr:nth-child(even) {
  background: #fffaf6;
}

.subject-table tbody tr:hover {
  background: #fff3ea;
}

@media (max-width: 960px) {
  .hero-header {
    padding: 24px 20px 20px;
  }

  .content-shell {
    padding: 20px;
  }

  .schedule-wrap {
    overflow-x: auto;
  }

  .schedule-grid {
    width: max(100%, 1137px);
    min-width: 0;
    grid-template-columns: 90px repeat(13, minmax(79px, 1fr));
  }
}

@media (max-width: 600px) {
  .room-timetable,
  .summary-panel,
  .schedule-panel,
  .table-panel {
    border-radius: 10px;
  }

  .content-shell {
    padding: 16px;
  }

  .search-btn {
    max-width: none;
  }

  .download-actions {
    width: 100%;
    margin-left: 0;
    justify-content: flex-end;
  }
}


.summary-row { gap: 16px; }
.summary-row h2 { font-family: "Anuphan", sans-serif !important; font-weight: 700; color: #5d4036; }
.summary-panel { background: #fff8f4; }
.legend { align-items: center; }
.undergraduate { background: #e3f0df !important; color: #35553a !important; }
.graduate { background: #eae0f7 !important; color: #594273 !important; }
.meeting, .level-cell { white-space: nowrap; }
</style>

<style scoped src="./timetableBlocks.css"></style>
<style scoped src="./timetableInteraction.css"></style>
